import React, { useMemo } from 'react';
import {
  Box,
  Contact,
  Field,
  FieldTypes,
  Inscription,
  RadioOption,
  Text,
} from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';


interface Args {
  data?: Partial<Contact>;
  firstContact?: boolean;
  onlyOnePrimaryContact?: boolean;
  viewMode?: boolean;
  errors?: {
    [key: string]: string
  }
}
interface ExtendedRadioOption extends RadioOption {
  id: number;
  key: string;
  title: string;
}

export default (args: Args, permissionsList: ExtendedRadioOption[]) => {
  const { errors = {} } = args;
  const temporaryPasswordField: Field[] = useMemo(() => (!args.viewMode
    ? ([{
      name: 'temporaryPassword',
      type: FieldTypes.Node,
      label: 'Password',
      value: (
        <Text
          weight="normal"
          color="textSecondary"
        >
          After adding a user, a password will be generated automatically
        </Text>
      ),
    }]) : []), [args.viewMode]);
  const userNameValidation = useMemo(() => {
    if (errors.username) {
      return VALIDATORS.REQUIRED_STRING.test({
        test: (val) => val !== errors.username,
        message: 'Username format is invalid',
      });
    }
    if (errors.usernameExists) {
      return VALIDATORS.REQUIRED_STRING.test({
        test: (val) => val !== errors.usernameExists,
        message: 'Username format is already used',
      });
    }
    return VALIDATORS.REQUIRED_STRING;
  }, [errors]);
  const fields = useMemo((): Field[] => [
    {
      name: 'firstName',
      type: FieldTypes.Text,
      label: 'First name',
      placeholder: 'First name',
      showRequireIcon: true,
      validator:VALIDATORS.REQUIRED_STRING,
      defaultValue: args?.data?.firstName || '',
    },
    {
      name: 'lastName',
      type: FieldTypes.Text,
      label: 'Last name',
      placeholder: 'Last name',
      showRequireIcon: true,
      validator: VALIDATORS.REQUIRED_STRING,
      defaultValue: args?.data?.lastName || '',
    },
    {
      name: 'email',
      type: FieldTypes.Email,
      label: 'Email',
      placeholder: 'Email',
      showRequireIcon: true,
      validator: VALIDATORS.EMAIL_REQUIRED ,
      defaultValue: args?.data?.email || '',
    },
    {
      name: 'userName',
      type: FieldTypes.Text,
      label: !args.viewMode ? (
        <Box>
          <Text margin={{ bottom: 'xsmall' }} weight="bold">Username</Text>
          <Box>
            {!args?.data?.externalIdentifier
              && (
                <Text
                  weight="normal"
                  size="12px"
                  style={{ width: '230px' }}
                  color="textSecondary"
                >Username field can be set up only once and cannot be updated later.
                </Text>
              )}
          </Box>
        </Box>
      ) : 'Username',
      placeholder: 'Username',
      showRequireIcon: true,
      validator: userNameValidation,
      defaultValue: args?.data?.userName || '',
      disabled: !!args?.data?.id,
      isLowerCaseMode: true,
    },
    {
      name: 'phoneNumber',
      type: FieldTypes.PhoneNumber,
      label: 'Phone number',
      placeholder: '+1 (555) 555-1234',
      validator:
        errors.phone
          ?VALIDATORS.STRING.test({
            test: (val) => val !== errors.phone,
            message: 'Phone number is invalid',
          })
          : VALIDATORS.STRING,
      defaultValue: args?.data?.phone || '',
    },
    {
      name: 'title',
      type: FieldTypes.Text,
      label: 'Title/role',
      placeholder: 'Title/role',
      validator: VALIDATORS.STRING,
      defaultValue: args?.data?.title || '',
    },
    {
      name: 'department',
      type: FieldTypes.Text,
      label: 'Department',
      placeholder: 'Department',
      validator: VALIDATORS.STRING,
      defaultValue: args?.data?.department || '',
    },
    ...temporaryPasswordField,
    {
      name: 'primary',
      type: FieldTypes.Checkbox,
      label: 'Set as primary contact',
      placeholder: 'Doing Business As',
      validator: VALIDATORS.BOOLEAN_REQUIRED,
      defaultValue: args?.firstContact ? true : (args?.data?.primary || false),
      disabled: args?.firstContact || Boolean(args?.onlyOnePrimaryContact && args?.data?.primary),
    },
    {
      name: 'roleId',
      type: FieldTypes.Dropdown,
      singleMode: true,
      isPositionSensitive: true,
      options: permissionsList
        .map((item) => ({
          key: item.key,
          value: item.title
          
        })),
      label: (
        <Box margin={{ top: 'spacing8' }} direction="row">
          <Inscription
            color="danger"
            margin={{
              right: '3px',
              top: '1px',
            }}
          >
            *
          </Inscription>
          <Text weight="bold">Permissions</Text>
        </Box>
      ),
      placeholder: 'Select permission',
      validator: VALIDATORS.REQUIRED_STRING,
      defaultValue: args?.data?.roleId,
      disabled: args?.firstContact || args?.data?.primary,
    },
  ], [
    args,
    errors,
    permissionsList,
    userNameValidation,
    temporaryPasswordField,
  ]);
  return fields;
};
